import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Clock, Filter, Inbox, Loader2, MessageSquarePlus, Plus, Search, Settings, Zap,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import type { CategoriaMensagem } from '@/services/mensagens'
import {
  getCanal, horasRestantes, janelaAberta, listConversas, nomeConversa,
  simularRecebida, type CanalConexao, type Conversa,
} from '@/services/atendimento'
import NovaConversaModal from './NovaConversaModal'

type Filtro =
  | 'todas' | 'nao_lidas' | 'minhas' | 'nao_atribuidas' | 'novas'
  | 'aguardando_medico' | 'aguardando_secretaria' | 'janela_fechada' | 'resolvidas'
  | CategoriaMensagem

const FILTROS_LINHA1: [Filtro, string][] = [
  ['todas', 'Todas'],
  ['nao_lidas', 'Não lidas'],
  ['minhas', 'Minhas conversas'],
  ['nao_atribuidas', 'Não atribuídas'],
]
const FILTROS_LINHA2: [Filtro, string][] = [
  ['novas', 'Novas'],
  ['aguardando_medico', 'Aguardando médico'],
  ['aguardando_secretaria', 'Aguardando secretária'],
  ['janela_fechada', 'Janela fechada'],
  ['resolvidas', 'Resolvidas'],
]
const FILTROS_CATEGORIA: [Filtro, string][] = [
  ['duvida_medicacao', 'Medicação'],
  ['envio_exame', 'Exames'],
  ['solicitacao_receita', 'Receita'],
  ['novo_sintoma', 'Sintoma'],
  ['intercorrencia', 'Intercorrência'],
  ['duvida_administrativa', 'Administrativa'],
]

export default function ListaConversas() {
  const { current, user } = useAuth()
  const navigate = useNavigate()
  const params = useParams()
  const segmentoAtivo = params.id ?? null

  const [conversas, setConversas] = useState<Conversa[]>([])
  const [canal, setCanal] = useState<CanalConexao | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [aba, setAba] = useState<'inbox' | 'historico'>('inbox')
  const [filtro, setFiltro] = useState<Filtro>('todas')
  const [busca, setBusca] = useState('')

  const [novaAberta, setNovaAberta] = useState(false)
  const [simAberto, setSimAberto] = useState(false)
  const [simTel, setSimTel] = useState('')
  const [simNome, setSimNome] = useState('')
  const [simTexto, setSimTexto] = useState('')
  const [simBusy, setSimBusy] = useState(false)

  const carregar = useCallback(async () => {
    if (!current) return
    setErro(null)
    try {
      const [cs, ch] = await Promise.all([
        listConversas(current.workspace.id, { aba: aba === 'historico' ? 'encerradas' : 'todas' }),
        getCanal(current.workspace.id),
      ])
      setConversas(cs)
      setCanal(ch)
    } catch {
      setErro('Não foi possível carregar. A migração phase8 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current, aba])

  useEffect(() => { carregar() }, [carregar])
  useEffect(() => {
    const t = setInterval(carregar, 15000)
    return () => clearInterval(t)
  }, [carregar])

  // contadores por filtro (calculados sobre a aba atual)
  const conta = useMemo(() => {
    const c: Record<string, number> = {}
    const fechada = (x: Conversa) => !janelaAberta(x)
    c.todas = conversas.length
    c.nao_lidas = conversas.filter(x => x.nao_lidas > 0).length
    c.minhas = conversas.filter(x => x.atendente_user_id === user?.id).length
    c.nao_atribuidas = conversas.filter(x => !x.atendente_user_id).length
    c.novas = conversas.filter(x => x.status === 'recebida').length
    c.aguardando_medico = conversas.filter(x => x.status === 'aguardando_medico' || x.status === 'aguardando_aprovacao').length
    c.aguardando_secretaria = conversas.filter(x => x.status === 'aguardando_secretaria').length
    c.janela_fechada = conversas.filter(fechada).length
    c.resolvidas = conversas.filter(x => x.status === 'respondida').length
    for (const [k] of FILTROS_CATEGORIA) c[k] = conversas.filter(x => x.categoria === k).length
    return c
  }, [conversas, user?.id])

  const visiveis = useMemo(() => {
    let rows = conversas
    switch (filtro) {
      case 'nao_lidas': rows = rows.filter(x => x.nao_lidas > 0); break
      case 'minhas': rows = rows.filter(x => x.atendente_user_id === user?.id); break
      case 'nao_atribuidas': rows = rows.filter(x => !x.atendente_user_id); break
      case 'novas': rows = rows.filter(x => x.status === 'recebida'); break
      case 'aguardando_medico': rows = rows.filter(x => x.status === 'aguardando_medico' || x.status === 'aguardando_aprovacao'); break
      case 'aguardando_secretaria': rows = rows.filter(x => x.status === 'aguardando_secretaria'); break
      case 'janela_fechada': rows = rows.filter(x => !janelaAberta(x)); break
      case 'resolvidas': rows = rows.filter(x => x.status === 'respondida'); break
      case 'todas': break
      default: rows = rows.filter(x => x.categoria === filtro)
    }
    if (busca.trim()) {
      const b = busca.trim().toLowerCase()
      rows = rows.filter(x => nomeConversa(x).toLowerCase().includes(b) || (x.wa_telefone ?? '').includes(b.replace(/\D/g, '')))
    }
    return rows
  }, [conversas, filtro, busca, user?.id])

  const abertas = conversas.filter(x => !['respondida', 'encerrada', 'convertida_tarefa'].includes(x.status)).length
  const naoLidas = conversas.reduce((s, x) => s + (x.nao_lidas ?? 0), 0)
  const comJanela = conversas.filter(x => janelaAberta(x)).length

  async function simular(e: React.FormEvent) {
    e.preventDefault()
    setSimBusy(true)
    const r = await simularRecebida({ telefone: simTel, texto: simTexto, nome: simNome || undefined })
    setSimBusy(false)
    if (!r.ok) { setErro(r.erro ?? 'falha'); return }
    setSimTexto('')
    await carregar()
    if (r.threadId) navigate(`/atendimento/${r.threadId}`)
  }

  function Pill({ id, label }: { id: Filtro; label: string }) {
    const n = conta[id] ?? 0
    return (
      <button className="pill" aria-pressed={filtro === id} onClick={() => setFiltro(id)}>
        {label}
        {n > 0 && <span className={`pill-badge${id === 'todas' || filtro === id ? '' : n && ['nao_lidas', 'novas', 'nao_atribuidas'].includes(id) ? '' : ' pill-badge--neutro'}`}>{n}</span>}
      </button>
    )
  }

  return (
    <aside className="atend-lista">
      <div className="atend-lista-topo">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}>
          <h1 style={{ fontSize: 19, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Caixa de Entrada</h1>
          <div style={{ display: 'flex', gap: 6 }}>
            <Link
              to="/configuracoes/whatsapp"
              className="btn-secondary"
              style={{ minHeight: 36, width: 36, padding: 0, justifyContent: 'center' }}
              aria-label="Configurar conexão"
            >
              <Settings size={16} aria-hidden />
            </Link>
            {canal && (
              <button
                className="btn-primary"
                style={{ minHeight: 36, width: 36, padding: 0, justifyContent: 'center' }}
                onClick={() => setNovaAberta(true)}
                aria-label="Nova conversa"
                title="Nova conversa"
              >
                <MessageSquarePlus size={17} aria-hidden />
              </button>
            )}
            {canal?.provedor === 'simulado' && (
              <button
                className="btn-primary"
                style={{ minHeight: 36, width: 36, padding: 0, justifyContent: 'center' }}
                onClick={() => setSimAberto(v => !v)}
                aria-label="Simular mensagem recebida"
                title="Simular mensagem recebida"
              >
                <Plus size={18} aria-hidden />
              </button>
            )}
          </div>
        </div>

        <div className="atend-abas" role="tablist" aria-label="Caixa de entrada e histórico">
          <button className="atend-aba" role="tab" aria-selected={aba === 'inbox'} onClick={() => setAba('inbox')}>Caixa de entrada</button>
          <button className="atend-aba" role="tab" aria-selected={aba === 'historico'} onClick={() => setAba('historico')}>Histórico</button>
        </div>

        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={15} aria-hidden style={{ position: 'absolute', left: 12, top: 13, color: 'var(--muted)' }} />
          <input
            className="field-input" style={{ paddingLeft: 34, minHeight: 40, fontSize: 13.5 }}
            placeholder="Buscar nome, telefone, assunto…"
            value={busca} onChange={e => setBusca(e.target.value)} aria-label="Buscar conversa"
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontWeight: 600 }}>
            <Filter size={13} aria-hidden /> Filtros
          </span>
          <span style={{ color: 'var(--muted)' }}>{abertas} abertas</span>
          {naoLidas > 0 && <span className="badge badge-danger" style={{ fontSize: 11 }}>{naoLidas} não lidas</span>}
          <span className="badge badge-ok" style={{ fontSize: 11, marginLeft: 'auto' }}>
            <Clock size={10} aria-hidden /> {comJanela} na janela
          </span>
        </div>

        <select
          className="field-input"
          style={{ minHeight: 40, fontSize: 13.5, marginBottom: 10 }}
          value={canal?.id ?? ''}
          onChange={() => {}}
          aria-label="Canal"
          disabled
        >
          <option value={canal?.id ?? ''}>
            {canal ? `WhatsApp: ${canal.nome}${canal.provedor === 'simulado' ? ' (simulado)' : ''}` : 'Nenhum canal configurado'}
          </option>
        </select>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {FILTROS_LINHA1.map(([id, label]) => <Pill key={id} id={id} label={label} />)}
          {FILTROS_LINHA2.map(([id, label]) => <Pill key={id} id={id} label={label} />)}
          {FILTROS_CATEGORIA.filter(([id]) => (conta[id] ?? 0) > 0 || filtro === id).map(([id, label]) => (
            <Pill key={id} id={id} label={label} />
          ))}
        </div>
      </div>

      {novaAberta && canal && current && (
        <NovaConversaModal
          workspaceId={current.workspace.id}
          canalId={canal.id}
          onClose={() => setNovaAberta(false)}
          onCreated={async threadId => {
            await carregar()
            navigate(`/atendimento/${threadId}`)
          }}
        />
      )}

      {simAberto && canal?.provedor === 'simulado' && (
        <form onSubmit={simular} style={{ padding: 14, borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <strong style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap size={14} aria-hidden /> Simular mensagem recebida
          </strong>
          <input className="field-input" style={{ minHeight: 38, fontSize: 13.5 }} placeholder="Telefone (48) 98000-0010" value={simTel} onChange={e => setSimTel(e.target.value)} aria-label="Telefone" />
          <input className="field-input" style={{ minHeight: 38, fontSize: 13.5 }} placeholder="Nome no WhatsApp (opcional)" value={simNome} onChange={e => setSimNome(e.target.value)} aria-label="Nome" />
          <textarea className="field-input" rows={2} style={{ resize: 'vertical', fontSize: 13.5 }} placeholder="Mensagem da paciente…" value={simTexto} onChange={e => setSimTexto(e.target.value)} aria-label="Mensagem" />
          <button type="submit" className="btn-primary" style={{ minHeight: 38, fontSize: 13.5 }} disabled={simBusy || !simTel || !simTexto}>
            {simBusy && <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
            Receber
          </button>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </form>
      )}

      <div className="atend-lista-rolagem">
        {erro && <div className="field-error" role="alert" style={{ padding: 16 }}>{erro}</div>}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          </div>
        ) : visiveis.length === 0 ? (
          <div style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--muted)' }}>
            <Inbox size={30} aria-hidden style={{ marginBottom: 10, opacity: .6 }} />
            <div style={{ fontSize: 14 }}>Nenhuma conversa.</div>
            {!canal && (
              <Link to="/configuracoes/whatsapp" style={{ fontSize: 13, color: 'var(--primary-deep)', fontWeight: 600, display: 'inline-block', marginTop: 8 }}>
                Configurar WhatsApp
              </Link>
            )}
          </div>
        ) : (
          visiveis.map(c => {
            const aberta = janelaAberta(c)
            const ativa = segmentoAtivo === c.id
            return (
              <Link
                key={c.id}
                to={`/atendimento/${c.id}`}
                className="conv-item"
                aria-current={ativa ? 'true' : undefined}
              >
                <span className={`conv-avatar${c.nao_lidas > 0 ? ' conv-avatar--nova' : ''}`} aria-hidden>
                  {nomeConversa(c).slice(0, 1).toUpperCase()}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <strong style={{ fontSize: 14, fontWeight: c.nao_lidas > 0 ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {nomeConversa(c)}
                    </strong>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)', flexShrink: 0 }}>
                      {c.ultima_mensagem_em
                        ? new Date(c.ultima_mensagem_em).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                        : ''}
                    </span>
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.ultima_mensagem_texto?.replace(/\s+/g, ' ').trim() || 'Sem mensagens'}
                    {c.atendente_user_id === user?.id ? ' · com você' : c.atendente_user_id ? ' · em atendimento' : ''}
                  </span>
                  <span style={{ display: 'flex', gap: 5, marginTop: 6, flexWrap: 'wrap' }}>
                    {!c.paciente_id && <span className="badge badge-warn" style={{ fontSize: 10 }}>não vinculada</span>}
                    <span className={`badge ${aberta ? 'badge-ok' : 'badge-muted'}`} style={{ fontSize: 10 }}>
                      <Clock size={9} aria-hidden />{aberta ? `${horasRestantes(c)}h` : 'janela fechada'}
                    </span>
                    {c.nao_lidas > 0 && <span className="pill-badge" style={{ marginLeft: 'auto' }}>{c.nao_lidas}</span>}
                  </span>
                </span>
              </Link>
            )
          })
        )}
      </div>
    </aside>
  )
}
